/**
 * RTK Query base API shared by every feature module.
 *
 * Mirrors `apps/admin-ui/src/store/api/apiSlice.ts`: a `fetchBaseQuery`
 * wrapped with automatic token refresh on 401 (single-flight refresh; queued
 * requests replay with the new token; failed refresh forces logout).
 *
 * Feature modules add endpoints WITHOUT touching this file via
 * `apiSlice.injectEndpoints(...)` inside `src/features/<name>/`. Tags are not
 * constrained by a `tagTypes` list, so features can introduce their own tag
 * names freely.
 */

import {
  createApi,
  fetchBaseQuery,
  type BaseQueryApi,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { getToken, refreshToken, clearAuth, markSessionExpired } from '@/lib/auth';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Track if a refresh is in progress to avoid concurrent refresh requests
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

/** Releases every queued request — `null` means the refresh failed. */
function flushSubscribers(token: string | null) {
  const subscribers = refreshSubscribers;
  refreshSubscribers = [];
  subscribers.forEach((callback) => callback(token));
}

function addRefreshSubscriber(callback: (token: string | null) => void) {
  refreshSubscribers.push(callback);
}

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
  timeout: 30_000,
});

function retryArgsWith(args: FetchArgs, token: string): FetchArgs {
  if (typeof args === 'string') {
    return { url: args, headers: { Authorization: `Bearer ${token}` } };
  }
  return {
    ...args,
    headers: {
      ...(args.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${token}`,
    },
  };
}

function isUnauthorized(result: { error?: unknown }): boolean {
  return !!result.error && (result.error as FetchBaseQueryError).status === 401;
}

/**
 * Drops the session and lets `ProtectedRoute` render the redirect — no hard
 * navigation, so the SPA keeps the `returnTo` state and the login page can show
 * the expiry notice (design §13.1's toast has no provider in this app yet).
 *
 * `auth/sessionExpired` is dispatched by type to keep this module free of an
 * import cycle with the auth slice (which depends on `apiSlice`).
 */
function forceLogout(api: BaseQueryApi) {
  clearAuth();
  markSessionExpired();
  api.dispatch({ type: 'auth/sessionExpired' });
}

/**
 * Custom baseQuery that wraps fetchBaseQuery with automatic token refresh on 401:
 * the original request is retried **exactly once** with the rotated token, and a
 * failed refresh logs the user out instead of looping.
 */
const baseQueryWithReauth: BaseQueryFn = async (args, api, extraOptions) => {
  const firstAttempt = await baseQuery(args, api, extraOptions);
  if (!isUnauthorized(firstAttempt)) {
    return firstAttempt;
  }

  if (isRefreshing) {
    // A refresh is already in flight: wait for it, then replay (or give up).
    return new Promise((resolve) => {
      addRefreshSubscriber((token) => {
        if (!token) {
          resolve(firstAttempt);
          return;
        }
        Promise.resolve(baseQuery(retryArgsWith(args, token), api, extraOptions)).then(resolve);
      });
    });
  }

  isRefreshing = true;
  let newToken: string | null = null;
  try {
    await refreshToken();
    newToken = getToken();
  } catch {
    newToken = null;
  } finally {
    isRefreshing = false;
    flushSubscribers(newToken);
  }

  if (!newToken) {
    forceLogout(api);
    return firstAttempt;
  }

  // One transparent retry with the rotated token.
  return baseQuery(retryArgsWith(args, newToken), api, extraOptions);
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
});

export type ApiSlice = typeof apiSlice;
